import {PixabayImage} from "./PixabayImage";
import API from "../../API";
import toaster from "../common/toaster";

class PixabaySearchService {
    constructor() {
        /** @type {string} A URL-encoded search query (max 100 chars). */
        this.query = "";

        /** @type {PixabayLanguage} Language to search in (default: "en"). */
        this.lang = Laravel.activeLanguage;

        /** @type {string|null} Retrieve a specific image by ID. */
        this.id = null;

        /** @type {PixabayImageType} Filter by image type. */
        this.imageType = "all";

        /** @type {PixabayOrientation} Filter by orientation. */
        this.orientation = "all";

        /** @type {PixabayCategory|null} Image category filter. */
        this.category = null;

        /** @type {number} Minimum width in px. */
        this.min_width = 0;

        /** @type {number} Minimum height in px. */
        this.min_height = 0;

        /** @type {PixabayColor[]|null} Color filters (comma-separated in request). */
        this.colors = null;

        /** @type {boolean} Editors' choice filter. */
        this.editors_choice = false;

        /** @type {boolean} Safe search mode. */
        this.safesearch = false;

        /** @type {PixabayOrder} Sort order. */
        this.order = "popular";

        /** @type {number} Page number (1-indexed). */
        this.page = 1;

        /** @type {number} Results per page (3–200). */
        this.perPage = 50;

        /** @type {boolean} */
        this.loading = false;

        /** @type {number} Total hits for query. */
        this.totalHits = 0;

        /** @type {number} Total returned images after pagination. */
        this.total = 0;

        /** @type {boolean} Whether a search already happened. */
        this.hasSearched = false;

        /** @type {PixabayImage[]} */
        this.images = [];

        this.pageCache = {};

        /** @type {string|null} JSONP callback (unused normally). */
        this.callback = null;

        /** @type {boolean} Pretty-print JSON output (never use in prod). */
        this.pretty = false;
    }

    setQuery(query) {
        this.query = query;
        this.page = 1;
    }

    setProperty(prop, value) {
        this[prop] = value;
    }

    buildRequestBody() {
        return {
            q: this.query,
            page: this.page,
            per_page: this.perPage,
            image_type: this.imageType,
            orientation: this.orientation,
            category: this.category,
            min_width: this.min_width,
            min_height: this.min_height,
            editors_choice: this.editors_choice,
            safesearch: this.safesearch,
            order: this.order
        };
    }

    async search() {
        if (this.loading) {
            return;
        }

        this.loading = true;

        const api = new API('/content/mediapool/pixabay/search');

        const body = this.buildRequestBody();
        const response = await api.post(body);

        if (response.errored) {
            toaster.error(response.data?.error);
            this.loading = false;
            return;
        }

        this.totalHits = response.totalHits | 0;
        this.total = response.total | 0;

        const hits = response.hits || [];
        const newImages = hits.map(img => new PixabayImage(img));

        this.pageCache[this.page] = newImages;
        this.images = newImages;

        this.loading = false;
        this.hasSearched = true;
    }

    reset() {
        this.query = "";
        this.page = 1;
        this.images = [];
        this.pageCache = {};
        this.totalHits = 0;
        this.hasSearched = false;
        this.editors_choice = false;
    }

    goToPage(page) {
        this.page = page;
        return this.search();
    }

    async nextPage() {
        if (this.page >= this.totalPages) return;

        this.page++;

        if (this.pageCache[this.page]) {
            this.images = this.pageCache[this.page];
            return;
        }

        return this.search(true);
    }

    async prevPage() {
        if (this.page <= 1) return;

        this.page--;

        if (this.pageCache[this.page]) {
            this.images = this.pageCache[this.page];
            return;
        }

        return this.search(true);
    }

    get totalPages() {
        return Math.ceil(this.total / this.perPage);
    }

}

export const pixabaySearchService = new PixabaySearchService();
